import type {
  FilterFieldGroupNode,
  FilterFieldLeafNode,
  FilterFieldListNode,
  FilterFieldNode,
  FilterFieldStatementNode,
} from '../types/tree-nodes.js';

/**
 * Check whether the given node is a filter field group node.
 * @internal
 */
export function isFilterFieldGroupNode(
  node: unknown,
): node is FilterFieldGroupNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldGroupNode).children !== undefined
  );
}

/**
 * Check whether the given node is a filter field statement node.
 * @internal
 */
export function isFilterFieldStatementNode(
  node: unknown,
): node is FilterFieldStatementNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldStatementNode).type === 'Statement'
  );
}

/**
 * Check whether the given node is a filter field list node.
 * @public
 */
export function isFilterFieldListNode(
  node: unknown,
): node is FilterFieldListNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldLeafNode).textValue !== undefined &&
    Array.isArray((node as FilterFieldLeafNode).value)
  );
}

/**
 * Check if the given token is a node holding a value.
 * Not necessarily a leaf node. E.g. a List has a value,
 * but the value consists of other tokens.
 * @internal
 */
export function isFilterFieldValueNode(
  node: unknown,
): node is FilterFieldLeafNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldLeafNode).textValue !== undefined
  );
}

/**
 * Check whether the given node is a filter field leaf node.
 * @public
 */
export function isFilterFieldLeafNode(
  node: unknown,
): node is FilterFieldLeafNode {
  return (
    typeof node === 'object' &&
    (node as FilterFieldLeafNode).textValue !== undefined &&
    !Array.isArray((node as FilterFieldLeafNode).value)
  );
}

/**
 * Check if the given token is a filter value.
 * @internal
 */
export function isFilterValue(
  token: FilterFieldLeafNode | FilterFieldListNode,
): boolean {
  const valueTypes: (FilterFieldLeafNode['type'] | 'List')[] = [
    'String',
    'Number',
    'Boolean',
    'Duration',
    'List',
    'Error',
  ];

  return valueTypes.includes(token.type);
}

/**
 * Returns whether the current operator is valid for a position operator (start-with / ends-with / contains).
 * Only equals, not equals, and ends-with/not-ends-with (will be converted to contains/not-contains) are valid values.
 * @internal
 */
export function isValidPositionOperator(
  node: FilterFieldStatementNode,
): boolean {
  return (
    node.operator?.value === '=' ||
    node.operator?.value === '!=' ||
    node.operator?.value === 'ends-with' ||
    node.operator?.value === 'not-ends-with'
  );
}

/**
 * Returns whether the current operator is a a LogicalOperator. Either it has the type LogicalOperator or it has the
 * type Error and 'OR' or 'AND' as value (if the LogicalOperator is in an invalid position or 'OR' is used in the basic syntax).
 * @internal
 */
export function isLogicalOperator(node: FilterFieldNode): boolean {
  return (
    node &&
    (node.type === 'LogicalOperator' ||
      (node.type === 'Error' && (node.value === 'OR' || node.value === 'AND')))
  );
}
